import { Check, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ResumeTemplate } from "@/lib/resumeTemplates";

interface TemplateCardProps {
  template: ResumeTemplate;
  isSelected: boolean;
  onSelect: (template: ResumeTemplate) => void;
  isRecommended?: boolean;
  recommendationReason?: string;
}

export function TemplateCard({ template, isSelected, onSelect, isRecommended = false, recommendationReason }: TemplateCardProps) {
  return (
    <button
      onClick={() => onSelect(template)}
      className={cn(
        "relative group rounded-xl overflow-hidden border-2 transition-all duration-200",
        "hover:shadow-lg hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        isSelected
          ? "border-primary shadow-md ring-2 ring-primary/20"
          : isRecommended
            ? "border-primary/60 ring-2 ring-primary/30 shadow-md"
            : "border-border hover:border-primary/50"
      )}
    >
      {isRecommended && (
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-primary text-primary-foreground text-[10px] font-semibold px-2 py-1 rounded-full shadow">
          <Sparkles className="w-3 h-3" />
          Recommended
        </div>
      )}

      {/* Preview Image */}
      <div className="aspect-[3/4] bg-muted relative overflow-hidden">
        <img
          src={template.previewImage}
          alt={`${template.name} template preview`}
          className="w-full h-full object-cover object-top"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder.svg';
          }}
        />

        <div className={cn(
          "absolute inset-0 bg-gradient-to-t from-black/60 to-transparent",
          "opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        )} />

        {isSelected && (
          <div className="absolute top-3 right-3 w-7 h-7 bg-primary rounded-full flex items-center justify-center shadow-lg">
            <Check className="w-4 h-4 text-primary-foreground" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3 bg-card text-left">
        <h3 className={cn(
          "font-semibold text-sm mb-1",
          isSelected ? "text-primary" : "text-foreground"
        )}>
          {template.name}
        </h3>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {template.description}
        </p>
        {isRecommended && recommendationReason && (
          <p className="text-[11px] text-primary mt-1.5 font-medium line-clamp-2">
            {recommendationReason}
          </p>
        )}
      </div>
    </button>
  );
}
